// DS1.9
import React, { AriaAttributes } from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import {
  UserNameClickBiEvent,
  UserNameHoverToOpenBiEvent,
} from '@wix/da-bi/pkg/events';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import BasicUserLink, {
  type BasicUserLinkProps,
} from '../_partials/BasicUserLink';
import BasicUserAvatar from '../_partials/BasicUserAvatar';

import s from './UserAvatar.scss';

export interface Props
  extends Pick<BasicUserLinkProps, 'linkTo' | 'anchor' | 'deviationIdSeed'> {
  user?: PapiUser;
  size: number;
  className?: string;
  avatarClassName?: string;
  withLink?: boolean;
  onTouchEnd?: any;
  biData?: Partial<UserNameClickBiEvent> | Partial<UserNameHoverToOpenBiEvent>;
  enableLazyLoad?: boolean;
  tabIndex?: 0 | -1;
  overlayIcon?: React.ReactNode;
  'aria-hidden'?: AriaAttributes['aria-hidden'];
}

function isClick(
  data?: Partial<UserNameClickBiEvent> | Partial<UserNameHoverToOpenBiEvent>
): data is UserNameClickBiEvent {
  return !data?.evid || data.evid === 101;
}

export const UserAvatar: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  size,
  withLink = true,
  linkTo,
  anchor = false,
  deviationIdSeed,
  className,
  avatarClassName,
  biData,
  'aria-hidden': ariaHidden,
  onTouchEnd,
  tabIndex,
  overlayIcon,
  enableLazyLoad,
}) => (
  <ErrorBoundary debugComponent="UserAvatar">
    {withLink && user ? (
      <BasicUserLink
        user={user}
        className={classnames(
          s['user-avatar'],
          overlayIcon !== null && s['has-overlay-icon'],
          className
        )}
        biData={isClick(biData) ? biData : undefined}
        aria-hidden={ariaHidden}
        onTouchEnd={onTouchEnd}
        tabIndex={tabIndex}
        anchor={anchor}
        linkTo={linkTo}
        deviationIdSeed={deviationIdSeed}
      >
        <BasicUserAvatar
          size={size}
          className={avatarClassName}
          user={user}
          aria-hidden={ariaHidden}
          enableLazyLoad={enableLazyLoad}
        />
        {overlayIcon && (
          <span className={s['overlay-icon']}>{overlayIcon}</span>
        )}
      </BasicUserLink>
    ) : (
      <span className={classnames(s['user-avatar'], className)}>
        <BasicUserAvatar
          size={size}
          user={user}
          className={avatarClassName}
          aria-hidden={ariaHidden}
          enableLazyLoad={enableLazyLoad}
        />
        {overlayIcon && (
          <span className={s['overlay-icon']}>{overlayIcon}</span>
        )}
      </span>
    )}
  </ErrorBoundary>
);
UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
